/**
 * Reading an identity out of an access token, for display.
 *
 * **This is not verification, and nothing here decides anything.** A browser
 * cannot check a signature it does not hold the key for, and even if it could,
 * an attacker controls the browser. Every access decision is the server's, and
 * it verifies the token itself on every request.
 *
 * What this is for: showing a name in the corner, deciding which controls to
 * grey out, and knowing when to renew. Getting any of it wrong produces a
 * cosmetic bug, never a hole.
 *
 * Pure: no network, no storage, no framework.
 */

/** A token whose contents could not be read at all. */
export class MalformedToken extends Error {
  constructor(why: string) {
    super(`This access token could not be read: ${why}`)
    this.name = 'MalformedToken'
  }
}

/** Claim names seen carrying an email address, most standard first. */
const EMAIL_CLAIMS = ['email', 'Email', 'email_address', 'upn', 'OdataUsername'] as const

/** Claim names seen carrying a username. */
const USERNAME_CLAIMS = ['preferred_username', 'username', 'UserName'] as const

/** Claim names seen carrying a display name. */
const NAME_CLAIMS = ['name', 'preferred_username', 'username', 'UserName'] as const

/** Both spellings, because both are in use. */
const ROLE_CLAIMS = ['role', 'roles'] as const

const EMAIL_SHAPED = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** A token's payload, before anything has been made of it. */
export type TokenPayload = Readonly<Record<string, unknown>>

/**
 * Decode a token's payload.
 *
 * Tokens are base64url encoded (RFC 4648 §5), not standard base64: `-` and `_`
 * stand in for `+` and `/`, and the padding is dropped. The payload can also
 * hold non-ASCII bytes, an accented name being the ordinary case, so `atob`
 * alone yields a mangled binary string. Both are handled here rather than at
 * each call site.
 */
export function decodePayload(token: string): TokenPayload {
  const segments = token.split('.')
  if (segments.length < 2) throw new MalformedToken('it is not in three parts')

  const segment = segments[1]
  if (!segment) throw new MalformedToken('its payload is empty')

  let base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
  while (base64.length % 4 !== 0) base64 += '='

  try {
    const binary = atob(base64)
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0))
    const parsed: unknown = JSON.parse(new TextDecoder('utf-8').decode(bytes))
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      throw new MalformedToken('its payload is not an object')
    }
    return parsed as TokenPayload
  } catch (cause) {
    if (cause instanceof MalformedToken) throw cause
    throw new MalformedToken('its payload is not valid encoded JSON')
  }
}

function firstString(payload: TokenPayload, names: readonly string[]): string {
  for (const name of names) {
    const value = payload[name]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

/**
 * The email address, wherever this provider decided to put it.
 *
 * Falls back to any claim whose value is shaped like an address. That is a
 * guess, and it is worth making: an invitation addressed to somebody's email
 * cannot reach them if this misses, and the cost of guessing wrong is that a
 * report is not shown as shared with somebody it was deliberately shared with.
 */
export function emailFrom(payload: TokenPayload): string {
  const direct = firstString(payload, EMAIL_CLAIMS)
  if (direct) return direct.toLowerCase()

  for (const value of Object.values(payload)) {
    if (typeof value === 'string' && EMAIL_SHAPED.test(value)) return value.toLowerCase()
  }
  return ''
}

/**
 * The roles, however they were expressed.
 *
 * A single role may arrive as a bare string rather than a list of one, which
 * is a serialisation convention and not a different meaning.
 */
export function rolesFrom(payload: TokenPayload): ReadonlySet<string> {
  const found = new Set<string>()
  for (const name of ROLE_CLAIMS) {
    const value = payload[name]
    if (typeof value === 'string' && value.trim()) {
      found.add(value.trim())
    } else if (Array.isArray(value)) {
      for (const entry of value) {
        if (typeof entry === 'string' && entry.trim()) found.add(entry.trim())
      }
    }
  }
  return found
}

/** Who a token says is holding it. */
export interface Identity {
  /** The stable subject. Ownership and authorship are anchored to this. */
  readonly id: string
  readonly username: string
  readonly email: string
  readonly name: string
  readonly roles: ReadonlySet<string>
  /** When the token stops being usable, in milliseconds since the epoch. */
  readonly expiresAt: number
}

/**
 * Interpret a token's claims.
 *
 * The username falls back through the display claims to the email, and finally
 * to a synthetic handle built from the subject. Something usable always comes
 * back: an interface showing a blank where a name belongs is worse than one
 * showing an opaque identifier, and the subject is at least stable and unique.
 */
export function readIdentity(payload: TokenPayload): Identity {
  const id = firstString(payload, ['sub', 'id'])
  const email = emailFrom(payload)
  const fallback = id ? `user-${id}` : 'anonymous'

  const username = firstString(payload, USERNAME_CLAIMS) || email || fallback
  const expiry = payload['exp']

  return {
    id,
    username,
    email,
    name: firstString(payload, NAME_CLAIMS) || username,
    roles: rolesFrom(payload),
    // Seconds on the wire, milliseconds everywhere in a browser. Mixing the
    // two silently treats a valid token as fifty years expired.
    expiresAt: typeof expiry === 'number' ? expiry * 1000 : 0,
  }
}

/** Read a token straight through, for callers that hold the string. */
export function identityFromToken(token: string): Identity {
  return readIdentity(decodePayload(token))
}
